import { ADVISOR_PREFILL_PREVIEW_METADATA_KEY, SessionType } from '@shared';

export type OnboardingGate = 'complete-intake' | 'results-locked' | null;

interface OnboardingGateClient {
  formReferenceId?: Client['formReferenceId'];
  formReferenceStatus?: Client['formReferenceStatus'];
  intakeSurvey?: Client['intakeSurvey'] | null;
  hasAccessToResults?: Client['hasAccessToResults'];
  metadata?: Client['metadata'];
}

interface ResolveOnboardingGateOptions {
  client: OnboardingGateClient | null | undefined;
  sessionType: SessionType;
  useFormProvider: boolean;
  isD2CMode: boolean;
  isGenworthMode: boolean;
  isEdjPilotAdvisor: boolean;
}

/**
 * Resolves the blocking onboarding screen in user-action order. An incomplete
 * intake must be actionable before a client is told to wait for result access.
 */
export function resolveOnboardingGate({
  client,
  sessionType,
  useFormProvider,
  isD2CMode,
  isGenworthMode,
  isEdjPilotAdvisor,
}: ResolveOnboardingGateOptions): OnboardingGate {
  if (!client) return null;

  const isClientSession = sessionType === SessionType.client;
  const hasIntakeSurvey = !!client.intakeSurvey;
  const isAdvisorPrefillPreview = client.metadata?.[ADVISOR_PREFILL_PREVIEW_METADATA_KEY] === true;
  const hasClientAnsweredIntake = hasIntakeSurvey && !(isClientSession && isAdvisorPrefillPreview);

  const needsIntake =
    !!client.formReferenceId &&
    client.formReferenceStatus !== 'completed' &&
    !hasClientAnsweredIntake &&
    (isClientSession || useFormProvider);

  if (needsIntake) return 'complete-intake';

  const resultsAreLocked =
    isClientSession &&
    client.hasAccessToResults !== true &&
    !isD2CMode &&
    !isGenworthMode &&
    !isEdjPilotAdvisor;

  return resultsAreLocked ? 'results-locked' : null;
}
